/**
 * Раздел «Архитектура»: уровни системы, взаимодействия и история изменений.
 *
 * Flow: App грузит опубликованный граф (GET /api/architecture) → раздел
 *       показывает узлы выбранного уровня → выбор узла раскрывает его связи
 *       и историю изменений.
 *
 * Полотна с перетаскиваемыми узлами намеренно нет: граф-библиотека весит сотни
 * килобайт и требует раскладки, которую всё равно правят руками. На трёх
 * десятках узлов уровней L1–L3 структурированный список с раскрытием связей
 * отвечает на те же вопросы и читается лучше.
 *
 * Зависимости:
 *   - react: выбор уровня и узла.
 *
 * Связанные компоненты:
 *   - App.jsx — грузит данные и переключает разделы.
 */
import { useMemo, useState } from 'react'

// Порядок значим: он же порядок уровней в переключателе.
const LEVELS = [
  { key: 'context', title: 'L1 · Контекст', hint: 'Люди, внешние системы и сама система' },
  { key: 'container', title: 'L2 · Контейнеры', hint: 'Сервисы и инфраструктура' },
  { key: 'component', title: 'L3 · Компоненты', hint: 'Пакеты внутри сервисов' },
  { key: 'interface', title: 'Интерфейсы', hint: 'Операции REST из контракта' },
  { key: 'governance', title: 'Ограничения', hint: 'Правила слоёв и запреты' },
]

const EDGE_TITLE = {
  uses: 'использует',
  depends_on: 'зависит от',
  db_access: 'доступ к БД',
  publishes: 'публикует',
  subscribes: 'подписан на',
}

function NodeDetail({ node, nodes, edges, history }) {
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]))
  const name = (id) => byId[id]?.name || id
  const outgoing = edges.filter((e) => e.from === node.id)
  const incoming = edges.filter((e) => e.to === node.id)
  const changes = history[node.id] || []

  return (
    <div className="arch-detail">
      <h3>{node.name}</h3>
      {node.description && <p className="arch-desc">{node.description}</p>}
      <div className="arch-meta">
        {node.technology && <span className="badge">{node.technology}</span>}
        {(node.tags || []).map((t) => <span className="badge" key={t}>{t}</span>)}
        {node.path && <code>{node.path}</code>}
        {node.files !== null && node.files !== undefined && <span className="ms-count">{node.files} файлов · {node.lines} строк</span>}
      </div>

      <h4>Взаимодействия</h4>
      {outgoing.length === 0 && incoming.length === 0 ? (
        <p className="ms-muted">Связей нет.</p>
      ) : (
        <table className="arch-edges">
          <tbody>
            {outgoing.map((e, i) => (
              <tr key={`o${i}`} className={e.violation ? 'arch-violation' : ''}>
                <td className="arch-dir">→</td>
                <td>{name(e.to)}</td>
                <td className="arch-kind">{EDGE_TITLE[e.kind] || e.kind}</td>
                <td className="ms-muted">{e.label}{e.technology ? ` · ${e.technology}` : ''}</td>
                {e.violation && <td className="arch-warn">{e.violation}</td>}
              </tr>
            ))}
            {incoming.map((e, i) => (
              <tr key={`i${i}`} className={e.violation ? 'arch-violation' : ''}>
                <td className="arch-dir">←</td>
                <td>{name(e.from)}</td>
                <td className="arch-kind">{EDGE_TITLE[e.kind] || e.kind}</td>
                <td className="ms-muted">{e.label}{e.technology ? ` · ${e.technology}` : ''}</td>
                {e.violation && <td className="arch-warn">{e.violation}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h4>История изменений</h4>
      {changes.length === 0 ? (
        <p className="ms-muted">Изменений не найдено.</p>
      ) : (
        <table className="arch-history">
          <tbody>
            {changes.map((c) => (
              <tr key={c.commit}>
                <td className="arch-commit">{c.commit}</td>
                <td className="arch-date">{c.date}</td>
                <td>{c.subject}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default function Architecture({ data }) {
  const [level, setLevel] = useState('container')
  const [selected, setSelected] = useState(null)

  const graph = data?.graph || {}
  const nodes = useMemo(() => graph.nodes || [], [graph])
  const edges = graph.edges || []
  const history = data?.history || {}

  const levelNodes = nodes.filter((n) => n.level === level)
  const node = nodes.find((n) => n.id === selected) || levelNodes[0]

  if (nodes.length === 0) {
    return (
      <div className="arch">
        <p className="ms-muted">
          Граф ещё не опубликован. Публикация — явный шаг:
          <code> python3 scripts/publish_architecture.py --apply</code>
        </p>
      </div>
    )
  }

  // Компоненты группируются по своему контейнеру: вложенность — то, что
  // отличает L3 от плоского перечня.
  const groups = level === 'component'
    ? Object.entries(levelNodes.reduce((acc, n) => {
      const key = n.parent || '—'
      acc[key] = acc[key] || []
      acc[key].push(n)
      return acc
    }, {}))
    : [[null, levelNodes]]

  const parentName = (id) => nodes.find((n) => n.id === id)?.name || id

  return (
    <div className="arch">
      <div className="arch-levels">
        {LEVELS.map((l) => (
          <button key={l.key} title={l.hint}
                  className={`btn ghost${level === l.key ? ' active' : ''}`}
                  onClick={() => { setLevel(l.key); setSelected(null) }}>
            {l.title} <span className="ms-count">{nodes.filter((n) => n.level === l.key).length}</span>
          </button>
        ))}
        <span className="ms-meta">
          опубликовано {data.published_at
            ? new Date(data.published_at).toLocaleString('ru-RU',
              { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
            : '—'}
          {data.published_commit ? ` · коммит ${data.published_commit}` : ''}
        </span>
      </div>

      <div className="arch-body">
        <div className="arch-list">
          {groups.map(([parent, items]) => (
            <div key={parent || 'all'}>
              {parent && <div className="arch-group">{parentName(parent)}</div>}
              {items.map((n) => (
                <button key={n.id} type="button"
                        className={`arch-node${node?.id === n.id ? ' active' : ''}`}
                        onClick={() => setSelected(n.id)}>
                  <span className="arch-node-name">{n.name}</span>
                  {n.technology && <span className="ms-count">{n.technology}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
        {node && <NodeDetail node={node} nodes={nodes} edges={edges} history={history} />}
      </div>
    </div>
  )
}
